import { Book, Chapter, BookSection } from '../types/book';
import { api } from './api';
import bookIndex from '../content/book-index-complete.json';
import { ContentLoader } from '../utils/contentLoader';

export const bookService = {
  async getBook(): Promise<Book> {
    try {
      // Transform the navigation structure into chapters/sections format
      const chapters: Chapter[] = [];
      
      // Process navigation items that are level 1 (chapters)
      for (const navItem of bookIndex.navigation) {
        if (navItem.level === 1) {
          const chapter: Chapter = {
            id: navItem.id,
            title: navItem.title,
            isExpanded: false,
            sections: []
          };
          
          // Add sections from children
          this.addSectionsFromNavigation(navItem.children || [], chapter, navItem.id);
          
          chapters.push(chapter);
        }
      }

      const book: Book = {
        title: bookIndex.title,
        metadata: bookIndex.metadata,
        chapters: chapters
      };

      return book;
    } catch (error) {
      console.error('Error loading book structure:', error);
      throw new Error('Failed to load book structure');
    }
  },

  // Helper method to recursively add sections from navigation
  addSectionsFromNavigation(navItems: any[], chapter: Chapter, parentId: string): void {
    for (const navItem of navItems) {
      const section: BookSection = {
        id: navItem.id,
        title: navItem.title,
        content: '', // Content loaded on demand
        level: navItem.level,
        parentId: parentId
      };
      
      chapter.sections.push(section);
      
      // Recursively add child sections
      if (navItem.children && navItem.children.length > 0) {
        this.addSectionsFromNavigation(navItem.children, chapter, parentId);
      }
    }
  },

  async getBookWithContent(): Promise<Book> {
    try {
      // This method loads the full book with all content for search indexing
      // Use sparingly to avoid context window issues
      const book = await this.getBook();
      
      // Load content for all sections in all chapters
      for (const chapter of book.chapters) {
        for (const section of chapter.sections) {
          try {
            // Load the actual content for each section
            const sectionWithContent = await this.getSection(section.id);
            section.content = sectionWithContent.content;
          } catch (error) {
            console.warn(`Failed to load content for section ${section.id}:`, error);
            // Keep empty content if loading fails
            section.content = '';
          }
        }
      }

      console.log(`Loaded book with ${book.chapters.length} chapters and ${book.chapters.reduce((total, ch) => total + ch.sections.length, 0)} sections for search indexing`);
      return book;
    } catch (error) {
      console.error('Failed to load book content:', error);
      throw new Error('Failed to load book content');
    }
  },

  async getChapter(chapterId: string): Promise<Chapter> {
    try {
      // Use the ContentLoader for efficient chapter loading
      return await ContentLoader.loadChapter(chapterId);
    } catch (error) {
      throw new Error('Failed to load chapter');
    }
  },

  async getSection(sectionId: string): Promise<BookSection> {
    try {
      // Load the section content using the new ContentLoader
      const content = await ContentLoader.loadSectionContent(sectionId);
      
      // Find the section metadata from the navigation
      const navItem = this.findNavigationItem(sectionId);
      if (!navItem) {
        throw new Error(`Section ${sectionId} not found in navigation`);
      }

      const section: BookSection = {
        id: sectionId,
        title: navItem.title,
        content: content,
        level: navItem.level,
        parentId: this.findParentId(sectionId) || ''
      };
      
      return section;
    } catch (error) {
      console.error('Error loading section:', error);
      throw new Error('Failed to load section');
    }
  },

  // Helper method to find navigation item by ID
  findNavigationItem(id: string): any {
    const findInNav = (navItems: any[]): any => {
      for (const item of navItems) {
        if (item.id === id) {
          return item;
        }
        if (item.children) {
          const found = findInNav(item.children);
          if (found) return found;
        }
      }
      return null;
    };
    
    return findInNav(bookIndex.navigation);
  },

  // Helper method to find parent ID for a section
  findParentId(sectionId: string): string | null {
    const findParent = (navItems: any[], _parentId: string | null = null): string | null => {
      for (const item of navItems) {
        if (item.children) {
          for (const child of item.children) {
            if (child.id === sectionId) {
              return item.id;
            }
          }
          const found = findParent(item.children, item.id);
          if (found) return found;
        }
      }
      return null;
    };
    
    return findParent(bookIndex.navigation);
  },

  async loadChapterContent(chapterId: string): Promise<string> {
    try {
      // Use the ContentLoader for efficient content loading
      return await ContentLoader.loadChapterContent(chapterId);
    } catch (error) {
      throw new Error('Failed to load chapter content');
    }
  },

  async searchBook(query: string): Promise<BookSection[]> {
    try {
      const response = await api.get(`/book/search?q=${encodeURIComponent(query)}`);
      return response.data;
    } catch (error) {
      throw new Error('Search failed');
    }
  },

  // Parse markdown content from the actual book file
  parseMarkdownToBook(markdownContent: string): Book {
    const lines = markdownContent.split('\n');
    const chapters: Chapter[] = [];
    let currentChapter: Chapter | null = null;
    let currentSection: BookSection | null = null;
    let contentBuffer: string[] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // Chapter detection (# Chapter X:)
      if (line.match(/^# Chapter \d+:/)) {
        // Save previous section if exists
        if (currentSection && contentBuffer.length > 0) {
          currentSection.content = contentBuffer.join('\n').trim();
          contentBuffer = [];
        }

        // Save previous chapter if exists
        if (currentChapter) {
          chapters.push(currentChapter);
        }

        // Create new chapter
        const chapterMatch = line.match(/^# (Chapter \d+: .+)$/);
        if (chapterMatch) {
          currentChapter = {
            id: `chapter-${chapters.length + 1}`,
            title: chapterMatch[1],
            sections: [],
            isExpanded: false
          };
          currentSection = null;
        }
      }
      // Section detection (## X.X)
      else if (line.match(/^## \d+\.\d+/)) {
        // Save previous section if exists
        if (currentSection && contentBuffer.length > 0) {
          currentSection.content = contentBuffer.join('\n').trim();
          if (currentChapter) {
            currentChapter.sections.push(currentSection);
          }
          contentBuffer = [];
        }

        // Create new section
        const sectionMatch = line.match(/^## (\d+\.\d+) (.+)$/);
        if (sectionMatch && currentChapter) {
          currentSection = {
            id: `section-${sectionMatch[1].replace('.', '-')}`,
            title: `${sectionMatch[1]} ${sectionMatch[2]}`,
            content: '',
            level: 2,
            parentId: currentChapter.id
          };
        }
      }
      // Content lines
      else if (line.length > 0 && currentSection) {
        contentBuffer.push(line);
      }
    }

    // Save final section and chapter
    if (currentSection && contentBuffer.length > 0) {
      currentSection.content = contentBuffer.join('\n').trim();
      if (currentChapter) {
        currentChapter.sections.push(currentSection);
      }
    }
    if (currentChapter) {
      chapters.push(currentChapter);
    }

    return {
      title: "Medical Device Cybersecurity Guide",
      chapters,
      metadata: {
        author: "CyberMed.ai",
        version: "1.0.0",
        lastUpdated: new Date().toISOString().split('T')[0]
      }
    };
  }
};
